/**
 * @module  experience/hardgoods
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */

/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
// Core
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

// Services
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { SharedBrowseService } from '@experience/shop/shared-browse/services/shared-browse.service';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';

// Model
import { CompareDeviceModel } from '@experience/shop/hardgoods/models/compare-device.model';
import { LoggerService } from '@core/logger/services/logger.service';
import { APIError } from '@support/api/common/error.interface';
import { HARDGOODS_MODULE_COMPONENTS } from '@experience/shop/hardgoods/constants/hardgoods.constants';
import { CONTENT_ENDPOINT } from '@core/aem/content-endpoints';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { BrowseDeviceModel } from '@experience/shop/hardgoods/models/browse-device.model';
import { SETTINGS } from '@environment/settings';

// Interface
import { ChannelSegment, CompareRequest } from '@support/api/common/product.interface';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { throwError as observableThrowError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
@Injectable()
export class CompareHardgoodsService {
  /**
   * Compare device data retrieves from shared services
   */
  public compareDeviceData: CompareDeviceModel[];
  /**
   * To save the state of compare page
   */
  public presentState: string;

  /**
   * To save the state of type of device
   */
  public filter: string;

  constructor(
    private router: Router,
    private contentProviderService: ContentProviderService,
    private sharedBrowseService: SharedBrowseService,
    private logger: LoggerService,
    private apiGateway: ApiGatewayService,
    private objectService: ObjectService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private commonUtilService: CommonUtilService
  ) {}

  /**
   * function to navigate provide route
   * @param route
   * @returns void
   */
  public navigate(route: string): void {
    this.router.navigate([route]);
  }

  /**
   * Retrieves comparison device data
   * @returns CompareDeviceModel[]
   */
  public getComparisonDeviceData(): Observable<BrowseDeviceModel[]> {
    const reqObj = this.createCompareRequest();
    return this.apiGateway.postRequest(API_MAP.API_2144_compareDevices, reqObj, null, null).pipe(
      map((response: HttpResponse<Object>) => {
        const res = response.body;
        const productDetails: BrowseDeviceModel[] = [];
        // tslint:disable-next-line: no-string-literal
        if (res['products']) {
          // tslint:disable-next-line: no-string-literal
          for (const product of res['products']) {
            productDetails.push(
              new BrowseDeviceModel(product, this.objectService, SETTINGS.EXT_IMAGE_URL)
            );
          }
        }
        return productDetails;
      }),
      catchError((error: APIError) => {
        return observableThrowError(error);
      })
    );
  }

  /**
   * Method to create request payload for API 2144
   * @returns void
   */
  private createCompareRequest(): CompareRequest {
    const productOfferingIds = this.commonUtilService.sortUtil(
      this.sharedBrowseService.selectedProductOfferIds,
      HARDGOODS_MODULE_COMPONENTS.OFFER_ID,
      true
    );
    const channelSegment: ChannelSegment = {};
    channelSegment.salesChannelCode = HARDGOODS_MODULE_COMPONENTS.UNASSISTED;
    channelSegment.salesSubChannelCode = HARDGOODS_MODULE_COMPONENTS.SELF_SERVICE_ANONYMOUS;
    channelSegment.subChannelCategory = HARDGOODS_MODULE_COMPONENTS.GENERAL_ANONYMOUS;
    const customerConfigData = this.sharedBrowseService.getUserInputsConfigData();
    const zip =
      this.objectService.get(this.stateStoreUtilsService.getBillingAddress(), 'postalCode') ||
      customerConfigData.zip;
    return {
      productDetails: productOfferingIds,
      channelSegment: channelSegment,
      orderType: HARDGOODS_MODULE_COMPONENTS.ACTIVATION,
      zipCode: zip,
    };
  }

  /**
   * Method to get AEM Content for DTA Landing Page
   * @return object
   */
  public getCompareDevicesContent(): Observable<object> {
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.COMPARE_HARDGOODS_SERVICE,
      'entering getCompareDevicesContent() in ' +
        HARDGOODS_MODULE_COMPONENTS.COMPARE_HARDGOODS_SERVICE
    );
    return this.contentProviderService
      .getContentData(CONTENT_ENDPOINT.COMPARE_DEVICES, 'shared', true)
      .pipe(
        map((contentData: object) => {
          const aemData = {
            configurableData: null,
            authorableData: null,
          };
          aemData.configurableData = <ConfigData>(
            this.contentProviderService.getConfigurableData(contentData)
          );
          aemData.authorableData = this.contentProviderService.getAuthorableData(contentData) || [];
          return aemData;
        }),
        catchError((error: APIError) => {
          this.logger.error(
            HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
            HARDGOODS_MODULE_COMPONENTS.COMPARE_HARDGOODS_SERVICE,
            ' API error while getting devices data in getCompareDevicesContent() in ' +
              HARDGOODS_MODULE_COMPONENTS.COMPARE_HARDGOODS_SERVICE +
              ' error: ' +
              JSON.stringify(error),
            error.code
          );
          return observableThrowError(error);
        })
      );
  }

  /**
   * function to filter the AEM data according to the type of device
   * @returns string
   */
  public filterContentUrl(): string {
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.COMPARE_HARDGOODS_SERVICE,
      'entering filterContentUrl() in ' + HARDGOODS_MODULE_COMPONENTS.COMPARE_HARDGOODS_SERVICE
    );
    const url = this.presentState;

    switch (url) {
      case '/browse-phones':
        this.filter = 'Devices';
        break;
      case '/browse-tablets':
        this.filter = 'Tablets';
        break;
      default:
        break;
    }
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.COMPARE_HARDGOODS_SERVICE,
      'leaving filterContentUrl() in ' +
        HARDGOODS_MODULE_COMPONENTS.COMPARE_HARDGOODS_SERVICE +
        ' returning filter: ' +
        this.filter
    );
    return this.filter;
  }
}
